import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import backgroundImage from '../../assets/images/hero.jpg';
import payImage from '../../assets/images/pay2.jpg';
import mockImage from '../../assets/images/mock.jpg';
import kpCardVideo from '../../assets/videos/KP-Card-Video.mp4';
import './KPCardHero.css';

// Hero content data
const heroContent = [
  {
    id: 1,
    background: backgroundImage,
    title: "Introducing the KP Card",
    subtitle: "Enjoy the convenience of cashless payment, simply visit the Keyplot Offices to get yours:",
    address: "Office 14\nGutenberg Platz\n51-55 Werner List Street,\nWindhoek CBD"
  },
  {
    id: 2,
    background: payImage,
    title: "Contactless\nPayments",
    subtitle: "Contactless payments are easy, fast, secure and convenient.\n\nMake use of the KP physical or virtual cards for your parking payments.",
    address: "Office 14\nGutenberg Platz\n51-55 Werner List Street,\nWindhoek CBD"
  },
  {
    id: 3,
    background: mockImage,
    title: "Download the KPI\nParking App",
    subtitle: "Forgot your card, do not worry, with your KPI parking application you can easily pay your parking from the KPI Parking App",
    address: "Office 14\nGutenberg Platz\n51-55 Werner List Street,\nWindhoek CBD"
  }
];

const HeroSection = styled.section`
  height: 95vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => `url(${props.image}) center/cover no-repeat`};
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2rem;
    padding: 0 15px;
  }
`;

const Content = styled.div`
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  margin-left: -4rem;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-left: 0;
    margin-bottom: 0.5rem;
    margin-top: 2rem;
    line-height: 1.1;
  }
`;

const AccentLine = styled(motion.div)`
  width: 60px;
  height: 3px;
  background: white;
  margin-bottom: 2rem;
  margin-left: -4rem;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
  margin-left: -4rem;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-left: 0;
    margin-bottom: 0.5rem;
    text-align: left;
    padding: 0 10px;
    line-height: 1.2;
    margin-top: 0.5rem;
  }
`;

const Address = styled(motion.div)`
  color: white;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-top: 2rem;
  margin-left: -4rem;
  white-space: pre-line;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 1rem;
    margin-top: 0.5rem;
    text-align: left;
    padding: 0 10px;
    line-height: 1.1;
  }
`;

const RedBox = styled.div`
  background: #FF0707;
  padding: 3rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 400px;
  box-shadow: 0 8px 32px rgba(255, 7, 7, 0.3);
`;

const LogoContainer = styled.div`
  text-align: right;
`;

const Logo = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const LogoText = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.9;
`;

const CallToAction = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.4;
  margin-top: auto;
`;

const VideoSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    margin-top: 2rem;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 476px;
  height: 476px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 100%;
    max-width: 220px;
    height: 220px;
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
`;

const SlideIndicators = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 3;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
`;

const KPCardHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate slides every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const currentContent = heroContent[currentSlide];

  return (
    <HeroSection>
      <AnimatePresence mode="wait">
        <BackgroundImage
          key={currentContent.id}
          image={currentContent.background}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
      </AnimatePresence>
      
      <Container>
        <Content>
          <AnimatePresence mode="wait">
            <Title
              key={`title-${currentContent.id}`}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              {currentContent.title}
            </Title>
          </AnimatePresence>
          
          <AnimatePresence mode="wait">
            <AccentLine
              key={`accent-${currentContent.id}`}
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 60 }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </AnimatePresence>
          
          <AnimatePresence mode="wait">
            <Subtitle
              key={`subtitle-${currentContent.id}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              {currentContent.subtitle}
            </Subtitle>
          </AnimatePresence>
          
          <AnimatePresence mode="wait">
            <Address
              key={`address-${currentContent.id}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              {currentContent.address}
            </Address>
          </AnimatePresence>
        </Content>
        
        <VideoSection>
          <VideoContainer>
            <Video autoPlay muted loop>
              <source src={kpCardVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </Video>
          </VideoContainer>
        </VideoSection>
      </Container>

      <SlideIndicators>
        {heroContent.map((_, index) => (
          <Indicator
            key={index}
            active={index === currentSlide}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </SlideIndicators>
    </HeroSection>
  );
};

export default KPCardHero; 